import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { TokenData } from '../../types';
import { useEthTokens } from '../../hooks/useEthTokens';
import { formatMarketCap } from '../../utils/address';
import { useRelayPrice } from '../../hooks/useRelayPrice';

// Helper function to convert HTTP URLs to HTTPS for mixed content security
const ensureHttpsUrl = (url: string | null | undefined, fallback: string): string => {
  if (!url) return fallback;

  // Handle IPFS hashes
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }

  // Handle ImgBB page URLs
  if (url.includes('ibb.co/')) {
    const match = url.match(/ibb\.co\/([a-zA-Z0-9]+)/);
    if (match) {
      return `https://i.ibb.co/${match[1]}.jpg`;
    }
  }

  // Handle img.bb URLs
  if (url.includes('img.bb')) {
    return url.startsWith('http://') ? url : `http://${url}`;
  }

  // Convert other HTTP URLs to HTTPS
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }

  if (!url.startsWith('http')) {
    return url;
  }

  return url;
};

// Helper function to format price with subscript for small values
const formatPriceWithSubscript = (price: number | null): React.ReactNode => {
  if (!price) return 'N/A';

  if (price >= 0.01) {
    return `$${price.toFixed(6)}`;
  }

  // For very small values, use subscript notation
  const priceStr = price.toFixed(20);
  const match = priceStr.match(/^0\.0*([1-9])/);

  if (match) {
    const zeros = priceStr.indexOf(match[1]) - 2;
    const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
    return (
      <>
        $0.0<sub>{zeros}</sub>{significantDigits}
      </>
    );
  }

  return `$${price.toFixed(6)}`;
};



// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';
const ETH_DARK = '#333333';
const ETH_LIGHT = '#FFFFFF';
const ETH_BLACK = '#000000';

const TokenListContainer = styled.div`
  background: transparent;
  padding: 32px 24px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 700px) {
    padding: 16px 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  @media (max-width: 600px) {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  margin: 0;
  
  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  background: #F5F5F5;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    background: #FFFFFF;
    border-color: ${ETH_BLUE};
    box-shadow: 0 0 0 2px rgba(98, 126, 234, 0.1);
  }

  &::placeholder {
    color: #999999;
  }
  @media (max-width: 600px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

const TokenListTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  width: 100%;
`;

const TokenListRow = styled.div<{ $hasPool?: boolean }>`
  display: grid;
  grid-template-columns: 40px 40px 1fr 140px 100px 80px;
  gap: 12px;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  align-items: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${props => props.$hasPool ? 'pointer' : 'default'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  &:hover {
    transform: ${props => props.$hasPool ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.$hasPool ? '0 8px 16px rgba(0, 0, 0, 0.05)' : '0 2px 4px rgba(0, 0, 0, 0.02)'};
    border-color: ${props => props.$hasPool ? ETH_BLUE : 'rgba(0, 0, 0, 0.05)'};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px;
    gap: 3px;
    padding: 12px 8px;
    font-size: 10px;
  }
`;

const TokenListHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 140px 100px 80px;
  gap: 12px;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #666666;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  
  .mobile-only {
    display: none;
  }
  
  .desktop-only {
    display: inline;
  }
  
  & > div:first-child,
  & > div:nth-child(3) {
    text-align: left;
  }
  
  & > div:nth-child(4),
  & > div:nth-child(5) {
    text-align: right;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px;
    gap: 3px;
    padding: 10px 4px;
    font-size: 8px;
    letter-spacing: 0.2px;
    
    .mobile-only {
      display: inline;
    }
    
    .desktop-only {
      display: none;
    }
  }
`;

const TokenRankCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #999999;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const TokenListLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const TokenListInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TokenListName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TokenListSymbol = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #666666;
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    font-size: 10px;
    gap: 4px;
  }
`;

const ContractAddressInline = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #999999;
  font-family: 'Monaco', 'Courier New', monospace;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: ${ETH_BLUE};
  }
  
  @media (max-width: 768px) {
    font-size: 8px;
    gap: 2px;
  }
`;

const CopyIconInline = styled.span`
  font-size: 10px;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  
  ${ContractAddressInline}:hover & {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    font-size: 8px;
  }
`;

const TokenListDataCell = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    font-size: 8px;
  }
`;

const TokenListValueCell = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 8px;
    white-space: nowrap;
    overflow: visible;
  }
`;

const QuickBuyButton = styled.a`
  background: ${ETH_LIGHT};
  border: 1px solid ${ETH_BLUE};
  border-radius: 6px;
  color: ${ETH_BLUE};
  font-size: 11px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px 12px;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: ${ETH_BLUE};
    color: #FFFFFF;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(98, 126, 234, 0.2);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    gap: 8px;
    margin-top: 24px;
  }
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#F5F5F5' : '#FFFFFF'};
  border: 1px solid ${props => props.disabled ? '#E0E0E0' : '#E0E0E0'};
  border-radius: 8px;
  color: ${props => props.disabled ? '#999999' : ETH_DARK};
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 8px 16px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: ${ETH_BLUE};
    color: ${ETH_BLUE};
  }
  
  @media (max-width: 600px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageNumberButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? ETH_BLUE : 'transparent'};
  border: 1px solid ${props => props.$active ? ETH_BLUE : 'transparent'};
  border-radius: 6px;
  color: ${props => props.$active ? '#FFFFFF' : '#666666'};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '400'};
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  padding: 6px 12px;
  transition: all 0.2s ease;
  min-width: 36px;
  
  &:hover {
    color: ${props => props.$active ? '#FFFFFF' : ETH_BLUE};
    background: ${props => props.$active ? ETH_BLUE : '#F5F5F5'};
  }
  
  @media (max-width: 600px) {
    padding: 4px 8px;
    font-size: 10px;
    min-width: 28px;
  }
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: #999999;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 600px) {
    font-size: 10px;
    width: 100%;
    text-align: center;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666666;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #ef4444;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999999;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
`;

export const EthTokenList: React.FC<{ searchQuery: string; onSearchChange: (query: string) => void }> = ({ searchQuery, onSearchChange }) => {
  const navigate = useNavigate();
  const {
    tokens,
    loading: tokensLoading,
    error: tokensError,
    currentPage,
    totalPages,
    goToPage,
    goToNextPage,
    goToPreviousPage
  } = useEthTokens(searchQuery);
  const { getPriceForToken } = useRelayPrice(tokens);

  if (tokensLoading) {
    return (
      <TokenListContainer>
        <LoadingMessage>Loading Ethereum tokens...</LoadingMessage>
      </TokenListContainer>
    );
  }

  if (tokensError) {
    return (
      <TokenListContainer>
        <ErrorMessage>Error: {tokensError}</ErrorMessage>
      </TokenListContainer>
    );
  }

  if (tokens.length === 0) {
    return (
      <TokenListContainer>
        <EmptyMessage>
          {searchQuery ? 'No tokens found matching your search.' : 'No tokens deployed on Ethereum yet.'}
        </EmptyMessage>
      </TokenListContainer>
    );
  }

  return (
    <TokenListContainer>
      <SectionHeader>
        <SectionTitle>Tokens</SectionTitle>
      </SectionHeader>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, ticker, address..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </SearchContainer>

      <TokenListTable>
        <TokenListHeader>
          <div>#</div>
          <div></div>
          <div>TOKEN</div>
          <div style={{ textAlign: 'right' }}>
            <span className="desktop-only">MARKET CAP</span>
            <span className="mobile-only">MC</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span className="desktop-only">PRICE</span>
            <span className="mobile-only">$</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <span className="desktop-only">TRADE</span>
            <span className="mobile-only">•</span>
          </div>
        </TokenListHeader>
        {tokens.map((token, index) => {
          const priceUSD = token.contract_address ? getPriceForToken(token.contract_address) : null;
          const marketCap = priceUSD ? formatMarketCap(priceUSD) : 'N/A';

          return (
            <TokenListRow key={index} $hasPool={!!token.pool_address}>
              <TokenRankCell>{index + 1 + (currentPage - 1) * 20}</TokenRankCell>
              <TokenListLogo
                src={ensureHttpsUrl(token.logo_url, 'https://placehold.co/32x32/333333/AAAAAA?text=Logo')}
                alt={`${token.name} Logo`}
                onClick={() => {
                  const addressToUse = token.pool_address || token.contract_address;
                  window.open(`https://www.dextools.io/app/en/ether/pair-explorer/${addressToUse}`, '_blank');
                }}
                style={{ cursor: 'pointer' }}
              />
              <TokenListInfoCell onClick={() => {
                const addressToUse = token.pool_address || token.contract_address;
                window.open(`https://www.dextools.io/app/en/ether/pair-explorer/${addressToUse}`, '_blank');
              }} style={{ cursor: 'pointer' }}>
                <TokenListName>{token.name}</TokenListName>
                <TokenListSymbol>
                  ${token.symbol}
                  <ContractAddressInline
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(token.contract_address);
                      // Show a brief visual feedback
                      const target = e.currentTarget.querySelector('span');
                      if (target) {
                        const originalText = target.textContent;
                        target.textContent = 'Copied!';
                        setTimeout(() => {
                          if (target) {
                            target.textContent = originalText;
                          }
                        }, 1000);
                      }
                    }}
                    title="Click to copy contract address"
                  >
                    <span>
                      {typeof window !== 'undefined' && window.innerWidth <= 768
                        ? `${token.contract_address.slice(0, 4)}...${token.contract_address.slice(-4)}`
                        : `${token.contract_address.slice(0, 6)}...${token.contract_address.slice(-4)}`
                      }
                    </span>
                    <CopyIconInline>📋</CopyIconInline>
                  </ContractAddressInline>
                </TokenListSymbol>
              </TokenListInfoCell>
              <TokenListValueCell>{marketCap}</TokenListValueCell>
              <TokenListValueCell>{formatPriceWithSubscript(priceUSD)}</TokenListValueCell>
              <TokenListDataCell>
                <QuickBuyButton
                  href={`https://app.uniswap.org/swap?chain=mainnet&inputCurrency=NATIVE&outputCurrency=${token.contract_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  title="Trade on Uniswap"
                  style={{
                    padding: window.innerWidth <= 768 ? '2px 4px' : '4px 8px',
                    fontSize: window.innerWidth <= 768 ? '8px' : '11px',
                    margin: '0 auto',
                    minWidth: window.innerWidth <= 768 ? '38px' : 'auto',
                    height: window.innerWidth <= 768 ? '20px' : 'auto'
                  }}
                >
                  Trade
                </QuickBuyButton>
              </TokenListDataCell>
            </TokenListRow>
          );
        })}
      </TokenListTable>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            title="Previous Page"
          >
            ←
          </PaginationButton>

          <PageNumbers>
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              if (startPage > 1) {
                pages.push(
                  <PageNumberButton key={1} onClick={() => goToPage(1)} $active={currentPage === 1}>
                    1
                  </PageNumberButton>
                );
                if (startPage > 2) {
                  pages.push(<span key="ellipsis1" style={{ color: '#bdbdbd', padding: '0 5px' }}>...</span>);
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <PageNumberButton
                    key={i}
                    onClick={() => goToPage(i)}
                    $active={currentPage === i}
                  >
                    {i}
                  </PageNumberButton>
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(<span key="ellipsis2" style={{ color: '#bdbdbd', padding: '0 5px' }}>...</span>);
                }
                pages.push(
                  <PageNumberButton
                    key={totalPages}
                    onClick={() => goToPage(totalPages)}
                    $active={currentPage === totalPages}
                  >
                    {totalPages}
                  </PageNumberButton>
                );
              }

              return pages;
            })()}
          </PageNumbers>

          <PaginationButton
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            title="Next Page"
          >
            →
          </PaginationButton>

          <PaginationInfo>
            Page {currentPage} of {totalPages}
          </PaginationInfo>
        </PaginationContainer>
      )}
    </TokenListContainer>
  );
};

export default EthTokenList;
